import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Modal, ModalBody, ModalContent, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { RoleContext } from '@/app/api/common';
import { deletedLocalUserPrivilege } from '@/features/deleted/deletedApi';
import { LoginDescriptor, LoginPrivileges, CategorizedPrivileges } from '@/features/logins/loginsSlice';
import { HotelInfo } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import CategorizedPrivilegeDropdown from '@/components/Common/CategorizedPrivilegeDropdown';
import DeletedPanel from '@/components/Common/DeletedPanel';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';
import { ButtonsModal, DeleteModal } from '@/components/Common/Modals';

import { CatHeader } from '@/pages/Projects/Project';

// ProjectLocalUsers — per-project "Users" tab. Local users are
// project-scoped accounts (role always "manager", no privileges, no display
// name) managed via the /local_users.json endpoint. The store owns the API
// calls (getLocalUsers / setLocalUser / resetLocalUserPassword /
// deleteLocalUser) and 2FA admin toggle (adminSetTotpDisabled); this
// component owns the project_id and the local list state (NOT in Redux —
// the list is small and scoped to the open project, so we refetch after
// every mutation rather than caching it globally).

interface ProjectLocalUsersProps {
  project:	HotelInfo;
}

const emailRegex = /^\S+@\S+\.\S+$/;

// What the form modal is doing. 'create' and 'edit' share the form;
// 'reset' swaps to a single new-password field.
type FormMode = 'create' | 'edit' | 'reset';

const ProjectLocalUsers: React.FC<ProjectLocalUsersProps> = ({ project }) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);

  const projectId = project.id;

  const canChange = role.admin || !!role?.privilages?.includes("ChangeLocalUsers");
  const canDelete = role.admin || !!role?.privilages?.includes("DeleteLocalUsers");
  // Server-wide 2FA capability (current session's /login.json descriptor).
  // The 2FA surfaces (status column + per-row toggle) only show when the
  // backend reports supports_2fa === true; false/absent → hidden.
  const supports2FA = useAppSelector((s) => s.logins.supports2FA);
  // Resetting a local user's 2FA is part of administering that user, so it
  // follows canChange rather than a separate gate. It previously required
  // ChangeGlobalUsers — a privilege that governs GLOBAL users and which a
  // project manager does not hold — so the enable/disable-2FA action was hidden
  // from exactly the people who administer these accounts, showing only for
  // admins.
  const can2FA = supports2FA && canChange;

  const [users, setUsers] = React.useState<LoginDescriptor[]>([]);
  // The CLOSED set of privileges a local user may hold, published by the server
  // on the list response. The picker is built from this — never a hardcoded
  // list — so it can never offer something the server rejects with code 12.
  const [assignable, setAssignable] = React.useState<LoginPrivileges[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as any[]);

  // Form modal state (create / edit / reset-password).
  const [formMode, setFormMode] = React.useState<FormMode | null>(null);
  const [formUser, setFormUser] = React.useState<LoginDescriptor | null>(null);

  // Delete + 2FA-toggle confirmations.
  const [deleteName, setDeleteName] = React.useState("");
  const [toggle2FA, setToggle2FA] = React.useState<{ username: string; disable: boolean } | null>(null);

  // Cell renderers reach back into the component via refs (mirrors the
  // global Users grid), so a row's action buttons call the live handlers.
  const requestEdit = React.useRef((u: LoginDescriptor) => { return; });
  const requestReset = React.useRef((u: LoginDescriptor) => { return; });
  const requestDelete = React.useRef((username: string) => { return; });
  const requestToggle2FA = React.useRef((username: string, disable: boolean) => { return; });

  const refresh = React.useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const payload = await store.getLocalUsers(projectId);
    setUsers(payload.descriptors);
    setAssignable(payload.assignable_privileges);
    setLoading(false);
  }, [projectId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Column defs — username, email, 2FA status, and (if permitted) options.
  React.useEffect(() => {
    setCols([
      {
        field:		"username",
        headerName:	t("projects:project:local_users:grid:username"),
        flex:		1,
        minWidth:	150,
      },
      {
        field:		"email",
        headerName:	t("projects:project:local_users:grid:email"),
        flex:		1,
        minWidth:	200,
      },
      {
        field:			"privileges",
        headerName:		t("projects:project:local_users:privileges:caption"),
        flex:			0,
        filter:			false,
        minWidth:		150,
        maxWidth:		150,
        cellRenderer:	PrivilegesCell,
      },
      ...(supports2FA
        ? [{
            field:		"twofa",
            headerName:	t("projects:project:local_users:grid:twofa"),
            flex:		0,
            minWidth:	150,
            maxWidth:	180,
          }]
        : []),
      ...(canChange || canDelete || can2FA)
        ? [{
            field:		"options",
            headerName:	t("projects:project:local_users:grid:options"),
            flex:		0,
            filter:		false,
            minWidth:	90,
            maxWidth:	90,
            cellRenderer:	OptionsCell,
          }]
        : [],
    ]);
  }, [t, canChange, canDelete, can2FA, supports2FA]);

  // Build the grid rows from the fetched users, tagging each with the action
  // refs and a derived 2FA status string.
  React.useEffect(() => {
    // The column reports the 2FA POLICY state — which is what the admin toggle
    // actually controls, and the only thing the wire tells us.
    //
    // GET /local_users.json does NOT return `totp_enrolled` (unlike
    // /logins.json for global users), so enrollment state is unknowable here.
    // Falling back to "Not enrolled" was actively misleading: after a
    // successful enable, totp_disabled flips to false and the row still read
    // "Not enrolled", making it look as though the action had done nothing.
    //
    // The totp_enrolled branch is kept so this upgrades for free the moment the
    // backend starts sending the flag.
    const twofaLabel = (u: LoginDescriptor): string => {
      if (u.totp_disabled) return t("projects:project:local_users:twofa:disabled");
      if (u.totp_enrolled) return t("projects:project:local_users:twofa:enrolled");
      return t("projects:project:local_users:twofa:enabled");
    };

    setRows(users.map((u) => ({
      ...u,
      twofa:				twofaLabel(u),
      // `privileges` may arrive as null or [] — treat them as equivalent.
      privileges:			u.privileges || [],
      // Carried so the cell can resolve a privilege id → its human description
      // without reaching for the global catalog (which a project manager can't
      // read — /logins.json is admin-gated).
      assignable,
      canChange,
      canDelete,
      can2FA,
      requestEdit,
      requestReset,
      requestDelete,
      requestToggle2FA,
    })));
  }, [users, t, canChange, canDelete, can2FA]);

  requestEdit.current = (u: LoginDescriptor) => { setFormUser(u); setFormMode('edit'); };
  requestReset.current = (u: LoginDescriptor) => { setFormUser(u); setFormMode('reset'); };
  requestDelete.current = (username: string) => setDeleteName(username);
  requestToggle2FA.current = (username: string, disable: boolean) => setToggle2FA({ username, disable });

  // Row click opens the edit drawer, mirroring the global Users grid. The
  // options column is excluded — it owns its own ⋮ menu, so a click there must
  // not also open the drawer behind the popover.
  const handleCellClick = React.useCallback((evt: any) => {
    if (evt?.colDef?.field === "options") return;
    if (!canChange) return;
    setFormUser(evt.data as LoginDescriptor);
    setFormMode('edit');
  }, [canChange]);

  const openCreate = () => {
    setFormUser({ username: "", password: "", email: "" } as LoginDescriptor);
    setFormMode('create');
  };

  const closeForm = () => { setFormMode(null); setFormUser(null); };

  const processDelete = React.useCallback(async (_obj: Object, answer: string) => {
    const username = deleteName;
    setDeleteName("");
    if (answer !== "yes" || !username) return;
    const ok = await store.deleteLocalUser(projectId, username);
    if (ok) refresh();
  }, [deleteName, projectId, refresh]);

  const processToggle2FA = React.useCallback(async (_obj: Object, answer: string) => {
    const req = toggle2FA;
    setToggle2FA(null);
    if (answer !== "yes" || !req) return;
    const ok = await store.adminSetTotpDisabled(req.username, req.disable, projectId);
    if (ok) refresh();
  }, [toggle2FA, projectId, refresh]);

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full gap-[8px]">
    <FlexRow direction="column" align="start" flex={0}>
      <div>
        <NumberText>{ users.length }</NumberText>
        { ' ' + t('projects:project:local_users:caption') }
      </div>
    </FlexRow>
    <Divider className="my-[4px]" />
    {canChange &&
      <Button caption={t('projects:project:local_users:new_user')}
              icon="add"
              className="uppercase"
              onClick={openCreate} />
    }
    <Grid rowData={rows}
          columnDefs={cols}
          filter={true}
          globalFilter={true}
          showPages={true}
          maxHeight="calc(100vh - 480px)"
          onCellClicked={handleCellClick}
          pageSize={10} />

    <DeletedPanel kind="user"
                  hotelId={projectId}
                  icon="group"
                  title={t('deleted:kind:local_user')}
                  subtitle={t('deleted:kind:local_user_hint')}
                  canView={role.admin || !!role?.privilages?.includes(deletedLocalUserPrivilege)}
                  onChanged={refresh} />

    {formMode && formUser &&
      <LocalUserFormModal mode={formMode}
                          projectId={projectId}
                          user={formUser}
                          assignable={assignable}
                          can2FA={can2FA}
                          onClose={closeForm}
                          onSaved={() => { closeForm(); refresh(); }} />
    }

    {deleteName !== "" &&
      <DeleteModal title={t('projects:project:local_users:delete:title')}
                   message={t('projects:project:local_users:delete:message', { u: deleteName })}
                   onResult={processDelete}>
        <></>
      </DeleteModal>
    }

    {toggle2FA &&
      <ButtonsModal title={toggle2FA.disable
                            ? t('projects:project:local_users:twofa:disable_title')
                            : t('projects:project:local_users:twofa:enable_title')}
                    message={toggle2FA.disable
                            ? t('projects:project:local_users:twofa:disable_message', { u: toggle2FA.username })
                            : t('projects:project:local_users:twofa:enable_message', { u: toggle2FA.username })}
                    buttons={[
                      { value: "no", caption: t('modals:buttons:cancel'), layout: "white" },
                      { value: "yes",
                        caption: toggle2FA.disable
                          ? t('projects:project:local_users:twofa:disable')
                          : t('projects:project:local_users:twofa:enable'),
                        layout: toggle2FA.disable ? "warning_bg" : "default" },
                    ]}
                    onResult={processToggle2FA}>
        <></>
      </ButtonsModal>
    }
  </FlexRow>
  );
};

// ---------------------------------------------------------------------------
// PrivilegesCell — count + hover-out popover listing the granted privileges,
// mirroring the global Users grid. Descriptions are resolved from the
// `assignable` list carried on the row: a project manager cannot read the
// global privilege catalog (/logins.json is admin-gated), so the local-users
// response is the only place those labels are available.
// ---------------------------------------------------------------------------
const PrivilegesCell = (props: any) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const granted: string[] = props.data?.privileges || [];
  const assignable: LoginPrivileges[] = props.data?.assignable || [];

  const descOf = (id: string) => assignable.find((p) => p.id === id)?.desc || id;

  return (
  <FlexRow justify="center">
    <span className="r14">
      { granted.length ? granted.length : "" }
    </span>
  { granted.length
    ?
  <Popover key={1}
           placement="bottom-end"
           onOpenChange={setOpen}
           radius="md"
           classNames={{ content: "rounded-none" }}>
    <PopoverTrigger>
      <div className="material-icons cursor-pointer !text-2xl !font-bold !text-[--Dark-Grey]">
        { open ? 'expand_less' : 'navigate_next' }
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[420px] r14">
        <FlexRow align="start" className="max-h-[320px] overflow-auto">
          <FlexRow direction="column" flex={1}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('users:privileges:privileges_desc') }
            </FlexRow>
          { granted.map((id: string, i: number) =>
            <FlexRow key={i} direction="column" align="start" className="p-[10px] gap-[2px]">
              <span className="text-[13px] text-black">{ id }</span>
              <span className="text-[12px] text-[--Dark-Grey] leading-[16px]">
                { descOf(id) }
              </span>
            </FlexRow>
          )}
          </FlexRow>
        </FlexRow>
      </FlexRow>
    </PopoverContent>
  </Popover>
    : null
  }
  </FlexRow>
  );
};

// ---------------------------------------------------------------------------
// OptionsCell — per-row action menu (edit / reset password / 2FA / delete),
// each item gated by the privilege flags carried on the row data.
// ---------------------------------------------------------------------------
const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const data = props.data;
  const disabled2FA = !!data.totp_disabled;

  const close = () => setOpen(false);

  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{ content: "rounded-none" }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[220px] py-[8px] gap-[8px]">
        {data.canChange &&
          <MenuItem icon="edit_note"
                    label={t('projects:project:local_users:edit')}
                    onClick={() => { close(); data.requestEdit.current(data); }} />
        }
        {data.canChange &&
          <MenuItem icon="lock_reset"
                    label={t('projects:project:local_users:reset_password:caption')}
                    onClick={() => { close(); data.requestReset.current(data); }} />
        }
        {data.can2FA &&
          <MenuItem icon="security"
                    label={disabled2FA
                            ? t('projects:project:local_users:twofa:enable')
                            : t('projects:project:local_users:twofa:disable')}
                    onClick={() => { close(); data.requestToggle2FA.current(data.username, !disabled2FA); }} />
        }
        {data.canDelete &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('projects:project:local_users:delete:caption')}
                    onClick={() => { close(); data.requestDelete.current(data.username); }} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

// ---------------------------------------------------------------------------
// LocalUserFormModal — create / edit / reset-password. apiOperation already
// surfaces server errors (e.g. "user already exists") and success toasts, so
// on a truthy store result we just close + refresh; on failure we keep the
// modal open (the toast explains why).
// ---------------------------------------------------------------------------
interface LocalUserFormModalProps {
  mode:			FormMode;
  projectId:	string;
  user:			LoginDescriptor;
  // Server-published closed set of privileges a local user may hold.
  assignable:	LoginPrivileges[];
  // Whether the caller may administer this user's 2FA.
  can2FA:		boolean;
  onClose:		() => void;
  onSaved:		() => void;
}

const LocalUserFormModal: React.FC<LocalUserFormModalProps> = ({ mode, projectId, user, assignable, can2FA, onClose, onSaved }) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);

  const [username, setUsername] = React.useState(user.username || "");
  const [password, setPassword] = React.useState("");
  const [email, setEmail] = React.useState(user.email || "");
  const [busy, setBusy] = React.useState(false);
  const [tried, setTried] = React.useState(false);

  // Admin 2FA toggle, mirroring the global User drawer. Only on 'edit' — the
  // backing record must exist server-side before 2FA can be enabled/disabled.
  const [confirm2FA, setConfirm2FA] = React.useState<null | 'enable' | 'disable'>(null);
  const show2FA = can2FA && mode === 'edit';

  // Privileges the local user currently holds. Treat null and [] as equivalent.
  const [privs, setPrivs] = React.useState<string[]>(user.privileges || []);

  // Delegation rule: the caller may only GRANT a privilege they hold themselves
  // (admins hold everything). Re-sending one the user already has is not an
  // escalation, and REVOKING is always allowed — so a privilege the user
  // already holds stays toggleable even when the caller couldn't grant it.
  const lockedIds = React.useMemo(() => {
    const canGrant = (p: string) => !!role.admin || !!role?.privilages?.includes(p);
    const alreadyHas = (p: string) => (user.privileges || []).includes(p);
    return assignable
      .filter((p) => !canGrant(p.id) && !alreadyHas(p.id))
      .map((p) => p.id);
  }, [assignable, role.admin, role?.privilages, user.privileges]);

  // The picker is categorised (same component as the global Users page), but
  // the server hands us a FLAT allowlist — so wrap it in a single category.
  const categorized: CategorizedPrivileges = React.useMemo(
    () => [{ category: 'LocalUser', privileges: assignable }],
    [assignable],
  );

  const usernameInvalid = mode === 'create' && (username.trim() === "" || username.includes("%"));
  const passwordInvalid = (mode === 'create' || mode === 'reset') && password.trim() === "";
  const emailInvalid = mode !== 'reset' && email.trim() !== "" && !emailRegex.test(email.trim());

  // CatHeader t()'s the key itself, so pass the key + interpolation params
  // rather than a pre-resolved string (mirrors User.tsx).
  const titleKey = mode === 'create'
    ? 'projects:project:local_users:create:title'
    : mode === 'reset'
      ? 'projects:project:local_users:reset_password:title'
      : 'projects:project:local_users:edit_title';

  const submit = React.useCallback(async () => {
    setTried(true);
    if (usernameInvalid || passwordInvalid || emailInvalid || busy) return;
    setBusy(true);
    try {
      let ok = false;
      if (mode === 'reset') {
        // reset_password preserves privileges server-side — nothing to re-send.
        ok = await store.resetLocalUserPassword(projectId, user.username, password.trim());
      } else {
        const desc = {
          username:	mode === 'create' ? username.trim() : user.username,
          password:	password,
          email:		email.trim(),
        } as LoginDescriptor;
        // This form RENDERS the picker, so it knows the intended set and always
        // sends the full array. An empty array here means the manager unticked
        // everything, i.e. revoke — which is correct. (Any code path that edits
        // a local user WITHOUT showing the picker must omit the argument, or it
        // would silently strip the user's grants.)
        ok = await store.setLocalUser(projectId, desc, mode === 'create', privs);
      }
      if (ok) onSaved();
    } finally {
      setBusy(false);
    }
  }, [mode, projectId, user.username, username, password, email, privs, usernameInvalid, passwordInvalid, emailInvalid, busy, onSaved]);

  // Right-side drawer, matching the global Users grid → <User/> shell
  // (Users.tsx): wrapper justify-end, full-height 690px-wide content, sticky
  // action bar pinned to the bottom.
  return (
  <Modal backdrop="opaque"
         defaultOpen={true}
         isDismissable={false}
         onClose={onClose}
         classNames={{
           backdrop:	"bg-[#0000007f]",
           wrapper:		"justify-end",
         }}
         hideCloseButton={true}
         radius="none"
         placement="top">
    <ModalContent className="h-full !mx-0 !my-0 w-full max-w-[690px]">
      {/* Flex column, NOT the global User page's fixed-height scroll region +
          absolute footer. That pattern is built for the long User form; for
          this short one it forced a full-height area (big empty gap) that
          overflowed the viewport into a scrollbar. Here the body is flex-1 and
          scrolls ONLY when it genuinely overflows; the footer sits at the
          bottom via flex, no absolute positioning. */}
      <ModalBody className="w-full h-full flex flex-col !p-0 overflow-hidden">
        <React.Fragment>
          <div className="flex-1 min-h-0 overflow-y-auto px-[32px] pt-[24px]">
            <div className="flex flex-col items-stretch pb-[24px] gap-[16px] w-full">
              <CatHeader caption={titleKey} captionProps={{ u: user.username }} />

              {mode === 'create' &&
                <Input label={t('projects:project:local_users:fields:username') + '*'}
                       placeholder={t('projects:project:local_users:fields:username')}
                       value={username}
                       onValueChange={setUsername}
                       autoComplete="off"
                       isInvalid={tried && usernameInvalid}
                       errorMessage={t('projects:project:local_users:fields:username_invalid')} />
              }
              {mode === 'edit' &&
                <Input label={t('projects:project:local_users:fields:username')}
                       value={user.username}
                       isDisabled={true}
                       isReadOnly={true} />
              }

              {(mode === 'create' || mode === 'reset') &&
                <Input label={(mode === 'reset'
                                ? t('projects:project:local_users:fields:new_password')
                                : t('projects:project:local_users:fields:password')) + '*'}
                       value={password}
                       onValueChange={setPassword}
                       type="password"
                       autoComplete="new-password"
                       isInvalid={tried && passwordInvalid}
                       errorMessage={t('projects:project:local_users:fields:password_invalid')} />
              }

              {mode !== 'reset' &&
                <Input label={t('projects:project:local_users:fields:email')}
                       placeholder="user@example.com"
                       value={email}
                       onValueChange={setEmail}
                       type="email"
                       autoComplete="off"
                       isInvalid={tried && emailInvalid}
                       errorMessage={t('projects:project:local_users:fields:email_invalid')} />
              }

              {/* Privileges — same picker as the global Users page. Built ONLY
                  from the server's assignable_privileges (never a hardcoded
                  list), so it can never offer something the server refuses with
                  code 12. Hidden on the reset-password form, whose endpoint
                  preserves grants. */}
              {mode !== 'reset' && assignable.length > 0 &&
                <CategorizedPrivilegeDropdown
                  label={t('projects:project:local_users:privileges:caption')}
                  privileges={categorized}
                  selectedPrivileges={privs}
                  onSelectionChange={setPrivs}
                  disabledPrivileges={lockedIds}
                  disabledReason={t('projects:project:local_users:privileges:cannot_grant')}
                />
              }

              {/* Two-factor administration — same card as the global User
                  drawer. Edit-only: the backing record must exist server-side
                  before 2FA can be enabled/disabled. */}
              {show2FA &&
                <FlexRow direction="column"
                         flex={0}
                         className="gap-[8px] mt-[8px] p-[12px] border border-[--Mid-Grey] rounded-[6px] bg-[--Light-Grey]">
                  <FlexRow flex={0} className="gap-[8px] items-center">
                    <span className="material-icons text-[--Primetime-main] text-[18px]">security</span>
                    <span className="r14 font-bold">
                      { t('projects:project:local_users:twofa:caption') }
                    </span>
                  </FlexRow>
                  <span className="r12 text-[--Dark-Grey]">
                    { user.totp_disabled
                        ? t('projects:project:local_users:twofa:hint_disabled')
                        : t('projects:project:local_users:twofa:hint_enabled') }
                  </span>
                  <FlexRow flex={0} className="gap-[8px]">
                    { user.totp_disabled
                        ? <Button caption={t('projects:project:local_users:twofa:enable')}
                                  icon="lock"
                                  isDisabled={busy}
                                  onClick={() => setConfirm2FA('enable')} />
                        : <Button caption={t('projects:project:local_users:twofa:disable')}
                                  icon="lock_open"
                                  isDisabled={busy}
                                  onClick={() => setConfirm2FA('disable')} />
                    }
                  </FlexRow>
                </FlexRow>
              }
            </div>
          </div>
          <FlexRow flex={0}
                   className="p-[24px] bg-white justify-end gap-[16px]">
            <Button className="uppercase"
                    caption={t('modals:buttons:cancel')}
                    onClick={onClose}
                    layout="white" />
            <Button className="uppercase"
                    caption={busy ? t('projects:project:local_users:saving') : t('projects:project:local_users:save')}
                    icon="save"
                    isDisabled={busy}
                    onClick={submit} />
          </FlexRow>
        </React.Fragment>
      </ModalBody>
    </ModalContent>

    {confirm2FA &&
      <ButtonsModal title={confirm2FA === 'disable'
                            ? t('projects:project:local_users:twofa:disable_title')
                            : t('projects:project:local_users:twofa:enable_title')}
                    message={confirm2FA === 'disable'
                            ? t('projects:project:local_users:twofa:disable_message', { u: user.username })
                            : t('projects:project:local_users:twofa:enable_message', { u: user.username })}
                    buttons={[
                      { value: "no", caption: t('modals:buttons:cancel'), layout: "white" },
                      { value: "yes",
                        caption: confirm2FA === 'disable'
                          ? t('projects:project:local_users:twofa:disable')
                          : t('projects:project:local_users:twofa:enable'),
                        layout: confirm2FA === 'disable' ? "warning_bg" : "default" },
                    ]}
                    onResult={async (_obj: Object, answer: string) => {
                      const target = confirm2FA;
                      setConfirm2FA(null);
                      if (answer !== 'yes') return;
                      setBusy(true);
                      try {
                        await store.adminSetTotpDisabled(user.username, target === 'disable', projectId);
                        // Close + refresh so the drawer reopens against the
                        // updated descriptor rather than a stale totp_disabled.
                        onSaved();
                      } finally {
                        setBusy(false);
                      }
                    }}>
        <></>
      </ButtonsModal>
    }
  </Modal>
  );
};

export default ProjectLocalUsers;
